import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { Lock, User, Shield, BarChart3, Zap } from 'lucide-react';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate('/clients', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/clients', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#09090b' }}>
      {/* Left Branding Panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden flex-col justify-between p-12"
        style={{ background: 'linear-gradient(135deg, #1e1145 0%, #2d1b69 30%, #1a0a3e 100%)' }}>
        {/* Grid pattern */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(139,92,246,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
        {/* Orbs */}
        <div className="absolute rounded-full animate-pulse" style={{ width: 200, height: 200, background: '#7c3aed', top: '15%', right: '10%', filter: 'blur(60px)', opacity: 0.4 }} />
        <div className="absolute rounded-full animate-pulse" style={{ width: 150, height: 150, background: '#6366f1', bottom: '20%', left: '15%', filter: 'blur(60px)', opacity: 0.4, animationDelay: '-3s' }} />
        <div className="absolute rounded-full animate-pulse" style={{ width: 100, height: 100, background: '#a78bfa', top: '55%', right: '30%', filter: 'blur(60px)', opacity: 0.4, animationDelay: '-5s' }} />

        <div className="relative z-10">
          <img src="/images/restroworks-logo-white.png" alt="Restroworks" style={{ height: 28, opacity: 0.9 }} />
          <h1 className="text-white font-bold mt-10" style={{ fontSize: 28, lineHeight: 1.3 }}>
            Secure Client<br />Management Portal
          </h1>
          <p className="mt-3" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, maxWidth: 320, lineHeight: 1.6 }}>
            Monitor and manage remote POS client connections with real-time metrics and alerts.
          </p>
        </div>

        <div className="relative z-10 space-y-1">
          {[
            { icon: <Shield size={18} />, text: 'End-to-end encrypted connections' },
            { icon: <BarChart3 size={18} />, text: 'Real-time system monitoring' },
            { icon: <Zap size={18} />, text: 'Instant remote access on demand' },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3 py-3" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
              <div className="flex items-center justify-center flex-shrink-0" style={{
                width: 36, height: 36, background: 'rgba(139,92,246,0.15)',
                border: '1px solid rgba(139,92,246,0.2)', borderRadius: 10, color: '#a78bfa'
              }}>
                {f.icon}
              </div>
              <span>{f.text}</span>
            </div>
          ))}
        </div>

        <div className="relative z-10" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
          Connect v1.0
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center px-6 relative">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.05) 0%, transparent 60%)'
        }} />

        <div className="w-full max-w-[380px] relative z-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl shadow-lg"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)', boxShadow: '0 4px 20px rgba(124,58,237,0.3)' }}>
              <img src="/images/restroworks-logo-white.png" alt="Restroworks" style={{ height: 32, display: 'block' }} />
            </div>
            <div className="inline-flex items-center gap-1.5 mt-4 px-3 py-1 rounded-full"
              style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', fontSize: 11, color: '#a78bfa' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
              Connect Portal
            </div>
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white">Welcome back</h2>
            <p className="text-sm mt-1" style={{ color: '#71717a' }}>Sign in to manage your client connections</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#a1a1aa' }}>Username</label>
              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#71717a' }} />
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="w-full pl-10 pr-3.5 py-2.5 rounded-xl text-sm outline-none transition-all"
                  style={{ background: '#fff', border: '1px solid #d4d4d8', color: '#09090b' }}
                  onFocus={e => { e.target.style.borderColor = '#7c3aed'; e.target.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)'; }}
                  onBlur={e => { e.target.style.borderColor = '#d4d4d8'; e.target.style.boxShadow = 'none'; }}
                  required
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#a1a1aa' }}>Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#71717a' }} />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-3.5 py-2.5 rounded-xl text-sm outline-none transition-all"
                  style={{ background: '#fff', border: '1px solid #d4d4d8', color: '#09090b' }}
                  onFocus={e => { e.target.style.borderColor = '#7c3aed'; e.target.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)'; }}
                  onBlur={e => { e.target.style.borderColor = '#d4d4d8'; e.target.style.boxShadow = 'none'; }}
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)', boxShadow: '0 4px 15px rgba(124,58,237,0.3)' }}
              onMouseOver={e => { (e.target as HTMLElement).style.boxShadow = '0 4px 20px rgba(124,58,237,0.5)'; (e.target as HTMLElement).style.transform = 'translateY(-1px)'; }}
              onMouseOut={e => { (e.target as HTMLElement).style.boxShadow = '0 4px 15px rgba(124,58,237,0.3)'; (e.target as HTMLElement).style.transform = 'translateY(0)'; }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <div className="my-5" style={{ height: 1, background: '#27272a' }} />
          <p className="text-center text-xs" style={{ color: '#3f3f46' }}>Secured with SSH key-based authentication</p>
        </div>
      </div>
    </div>
  );
}
